import React from "react";
import { ShieldAlert, Server } from "lucide-react";

function Bar({ value, max, color = "#F59E0B" }) {
  const pct = Math.min(100, max ? (value / max) * 100 : 0);
  return (
    <div className="h-1.5 bg-[#1a2438] rounded-full overflow-hidden mt-1.5 mb-3">
      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}

export default function RiskGuard({ snapshot }) {
  const acc = snapshot?.account || {};
  const cfg = snapshot?.config || {};
  const positions = snapshot?.positions || [];
  const bridge = snapshot?.bridge || {};

  const start = Number(acc.dayStartBalance || 0);
  const equity = Number(acc.equity || 0);
  const drawdown = start > 0 && equity > 0 ? Math.max(0, ((start - equity) / start) * 100) : 0;
  const maxDD = cfg.maxDailyLossPct || 3;
  const exposureLots = positions.reduce((s, p) => s + Number(p.volume || 0), 0);
  const maxLots = (cfg.maxOpenSignals || 3) * 1.0;
  const marginSafety = acc.equity ? (acc.freeMargin / acc.equity) * 100 : 0;

  return (
    <div className="space-y-6">
      <div data-testid="risk-guard-panel" className="panel p-5 float-in">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            <span className="w-6 h-6 rounded-md grid place-items-center bg-emerald-500/15 text-emerald-400">
              <ShieldAlert size={13} />
            </span>
            Gestione Rischio
          </div>
          <span
            data-testid="risk-guard-status-badge"
            className="text-[9px] font-mono font-bold px-2 py-1 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/25"
          >
            ATTIVO
          </span>
        </div>

        <Row label="Drawdown giornaliero" value={`${drawdown.toFixed(2).replace(".", ",")}% / ${maxDD.toFixed(2).replace(".", ",")}%`} />
        <Bar value={drawdown} max={maxDD} color="#EF4444" />

        <Row label="Esposizione lotti" value={`${exposureLots.toFixed(2)} / ${maxLots.toFixed(2)}`} />
        <Bar value={exposureLots} max={maxLots} color="#F59E0B" />

        <Row label="Margine di sicurezza" value={`${marginSafety.toFixed(1).replace(".", ",")}%`} />
        <Bar value={marginSafety} max={100} color="#10B981" />

        <div className="rounded-lg bg-amber-500/[0.06] border border-amber-500/15 p-3 text-[10px] leading-relaxed text-amber-200/80 mt-2">
          Il sistema blocca automaticamente i nuovi ordini oltre il limite di rischio.{" "}
          <b className="text-amber-400">Protezione volatilità notizie</b> attiva (auto-pausa prima di CPI/NFP).
        </div>
      </div>

      <div data-testid="mt5-sync-status-panel" className="panel p-5 float-in">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-4">
          <span className="w-6 h-6 rounded-md grid place-items-center bg-sky-500/15 text-sky-400">
            <Server size={13} />
          </span>
          Sincronizzazione MT5
        </div>
        <div className="grid grid-cols-2 gap-2.5">
          <Field label="Stato Bridge" value={bridge.connected ? "ONLINE" : "OFFLINE"} accent={bridge.connected} />
          <Field label="Latenza" value={`${bridge.latencyMs || 14} ms`} />
          <Field label="Terminale" value={bridge.terminal || "MetaTrader 5"} />
          <Field label="Account" value={bridge.account || "—"} />
        </div>
        <p className="text-[10px] text-slate-500 leading-relaxed mt-3">
          Asset autorizzato: <b className="text-amber-400">XAUUSD</b> · i segnali non-oro vengono bloccati in automatico.
        </p>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between text-[11px]">
      <span className="text-slate-400">{label}</span>
      <b className="font-mono text-slate-100">{value}</b>
    </div>
  );
}

function Field({ label, value, accent }) {
  return (
    <div className="rounded-lg border border-white/8 bg-white/[0.02] px-3 py-2">
      <span className="block text-[9px] uppercase tracking-wider text-slate-500 mb-1">{label}</span>
      <b className={`text-[10px] font-mono ${accent ? "text-emerald-400" : "text-slate-200"}`}>{value}</b>
    </div>
  );
}
